import React from "react";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import ButtonGroups, { BGItem } from "components/ButtonGroups";
import { REFERENCE_UNITS } from "features/MappingTools/consts";
import Button from "components/Button";
import { IconPlus } from "@tabler/icons-react";
import { componentsStore } from "store/Mapping/ComponentsStore";
import AddedChild from "./AddedChild";
import ProductSearch from "features/MappingTools/ProductSearch";
import useReorderList from "hooks/useReorderList";
import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";

const Step2 = () => {
  const { t } = useTranslation();

  const {
    moveDown,
    moveUp,
    items: reorderedList,
  } = useReorderList(componentsStore.data.childrenList);

  React.useEffect(() => {
    componentsStore.setData({
      ...componentsStore.data,
      childrenList: reorderedList,
    });
  }, [reorderedList]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = sortableItems.findIndex(
        (item) => item.sortableId === active.id
      );
      const newIndex = sortableItems.findIndex(
        (item) => item.sortableId === over?.id
      );

      const reorderedItems = arrayMove(
        componentsStore.data.childrenList,
        oldIndex,
        newIndex
      );

      componentsStore.setData({
        ...componentsStore.data,
        childrenList: reorderedItems,
      });
    }
  };

  const onClickAdd = () => {
    componentsStore.setOpenedSearchInCE(true);
  };

  const onChangeReferenceUnit = (value: BGItem | undefined) => {
    componentsStore.onChangeData("reference_unit", value);
  };

  const currentRef =
    REFERENCE_UNITS.find(
      (item) => item.id === componentsStore.data.reference_unit.id
    ) || REFERENCE_UNITS[0];

  const sortableItems = componentsStore.data.childrenList.map((row, index) => ({
    ...row,
    sortableId: `${row.id}-${index}`,
  }));

  return (
    <>
      <div className="text-lg font-semibold mb-4 mt-2">
        {t("components.refrenceUnitAndConstructionPRsCs")}
      </div>
      <div className="text-gray-700 text-sm mb-6">
        {t("components.step2Subtitle")}
      </div>
      <div className="bg-white border-gray-300 border p-6 rounded-md mb-6">
        <ButtonGroups
          items={REFERENCE_UNITS.filter((item) => item.id !== "KG")}
          onChange={onChangeReferenceUnit}
          current={currentRef}
          containerClassName="w-fit"
          label={`${t("components.reference_unit")}*`}
        />
        <div className="text-gray-700 text-sm mt-2">
          {t("components.reference_unit_desc")}
        </div>
      </div>
      <div className="flex flex-col items-center justify-center">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={sortableItems.map((item) => item.sortableId)}>
            {componentsStore.data.childrenList.map((item, index) => (
              <AddedChild
                childItem={item}
                key={index}
                rowIndex={index}
                moveDown={moveDown}
                moveUp={moveUp}
                sortableId={
                  sortableItems.find((_, inx) => inx === index)?.sortableId
                }
              />
            ))}
          </SortableContext>
        </DndContext>
        <div className="h-[98px] w-full border border-dashed border-indigo-600 rounded-md flex items-center justify-center">
          <Button
            type="secondary"
            width="fit-content"
            leadingIcon={<IconPlus />}
            onClick={onClickAdd}
          >
            {t("components.addProductComponent")}
          </Button>
        </div>
        <ProductSearch
          open={componentsStore.openedSearchInCE}
          close={() => componentsStore.setOpenedSearchInCE(false)}
        />
      </div>
    </>
  );
};

export default observer(Step2);
